import { apiUrl } from "../api/endpoints";
import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../hooks/AuthProvider";
import CollapseContext from "../hooks/CollapseContext";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import ArrowIcon from "@mui/icons-material/KeyboardArrowRight";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import MenuIcon from "@mui/icons-material/Menu";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import NorthWestIcon from "@mui/icons-material/NorthWest";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import DeviceThermostatOutlinedIcon from "@mui/icons-material/DeviceThermostatOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";


import { IconButton, Select, MenuItem } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import DefaultIcon from "./DefaultIcon";
import { useNavigate } from "react-router-dom";
import { EditUser } from "../Pages/EditUser";
import axios from "axios";
import { OrganizationIcon, UsersIcons, DeviceIcon, PasswordIcon } from "../Icons";
import { APP_NAME, APP_VERSION, STATIC_ICONS } from "../Constants";
import "./spinner.css";
import "./modal.css";
import "./header.css";
import { ChangeUserPassword } from "../Pages/Users";
import { EditOrganization } from "../Pages/EditOrganization";

import { LanguageContext } from "../context/LanguageContext";
import headerData from "../languages/header.json";
import systemNotificationsTranslations from "../languages/systemNotifications.json";
import { normalizeOrganizationListRow } from "../utils/listRowIds";
import {
  formatSystemNotificationTimestamp,
  localizeSystemNotification,
} from "../utils/systemNotificationText";

import Alert from "../Components/Alert";
import { getErrorMessage } from "../utils/errorUtils";

import { useFontSize } from "../context/FontSizeContext";
import { HeaderSettingsIcon } from "../svg/UtilsIcons";

import { useAppTheme } from "../context/ThemeContext";
import NotificationDetailsPopup from "./NotificationDetailsPopup";
import {
  AI_VOICE_ASSISTANT_POSITIONS,
  useAiVoiceAssistantSettings,
} from "../context/AiVoiceAssistantContext";
import { useBrowserVoiceCommandSettings } from "../context/BrowserVoiceCommandContext";

const ProjectInfo = ({ close, labels }) => {
  return (
    <div id="modal" onClick={close} className="project-info-modal">
      <div
        id="popup"
        className="dashboard-modal project-info-popup"
        onClick={(e) => e.stopPropagation()}
        aria-modal="true"
      >
        <div className="dashboard-header">
          <div className="logoHeader project-info-logoHeader">
            <div className="logoMark project-info-logoMark" aria-hidden="true">
              <div className="logoBar logoBarWhite" style={{ height: 15.194 }} />
              <div className="logoBar logoBarWhite" style={{ height: 23.542 }} />
              <div className="logoBar logoBarCyan" style={{ height: 34.294 }} />
              <div className="logoBar logoBarCyan" style={{ height: 29.088 }} />
              <div className="logoBar logoBarCyan" style={{ height: 17.602 }} />
              <div className="logoBar logoBarCyan" style={{ height: 11.253 }} />
              <div className="logoBar logoBarCyan" style={{ height: 5.721 }} />
              <div className="logoBar logoBarCyan" style={{ height: 3.269 }} />
            </div>
          </div>

          <div className="dashboard-header-text">
            <h2>{labels.projectInfo}</h2>
            <p>{labels.projectInfoDescription || "Application information and version details"}</p>
          </div>
        </div>

        <div className="modal-divider" />

        <div className="modal-content dashboard-content">
          <div className="form-section">
            <div className="form-item">
              <label className="form-lbl">{labels.name}</label>
              <input type="text" className="add-column" value={APP_NAME} readOnly />
            </div>
          </div>

          <div className="form-section">
            <div className="form-item">
              <label className="form-lbl">{labels.version}</label>
              <input type="text" className="add-column" value={APP_VERSION} readOnly />
            </div>
          </div>
        </div>

        <div className="modal-footer dashboard-footer">
          <button className="btn btn-cancel" type="button" onClick={close}>
            {labels.cancel}
          </button>
          <div />
        </div>
      </div>
    </div>
  );
};


const formatWeatherDetailValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value).replace(/_/g, " ");
};

const formatWeatherDetailDate = (value, locale) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(locale || "en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const WeatherRecordPopup = ({ close, data, labels, locale, isRtl }) => {
  const record = data?.record;
  if (!record) return null;

  const details = [
    [labels.weatherLocation || "Location", data?.location?.name || data?.location?.address || "-"],
    [labels.weatherSourceOrganization || "Organization source", data?.weatherContext?.sourceOrganizationName || "-"],
    [labels.weatherArea || "Weather area", record.emirateName || record.emirateNameAr || record.emirateCode || "-"],
    [labels.temperature || "Temperature", Number.isFinite(Number(record.temperatureC)) ? `${Number(record.temperatureC).toFixed(1)} °C` : "-"],
    [labels.humidity || "Humidity", Number.isFinite(Number(record.humidityPercent)) ? `${Math.round(Number(record.humidityPercent))}%` : "-"],
    [labels.weatherStatus || "Weather", formatWeatherDetailValue(record.weatherStatus)],
    [labels.airQuality || "Air quality", formatWeatherDetailValue(record.airQualityStatus)],
    [labels.dustStatus || "Dust", formatWeatherDetailValue(record.dustStatus)],
    [labels.usAqi || "US AQI", Number.isFinite(Number(record.usAqi)) ? Math.round(Number(record.usAqi)) : "-"],
    [labels.provider || "Provider", formatWeatherDetailValue(record.provider)],
    [labels.observedAt || "Observed", formatWeatherDetailDate(record.observedAt, locale)],
    [labels.fetchedAt || "Fetched", formatWeatherDetailDate(record.fetchedAt, locale)],
  ];

  return (
    <div id="modal" onClick={close} className="weather-record-modal" dir={isRtl ? "rtl" : "ltr"}>
      <div
        id="popup"
        className="weather-record-popup"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={labels.latestWeatherRecord || "Latest weather record"}
      >
        <div className="weather-record-popup-head">
          <div>
            <h2>{labels.latestWeatherRecord || "Latest weather record"}</h2>
            <p>{labels.latestWeatherRecordDescription || "Latest record stored by the weather cron for this location."}</p>
          </div>
          <button type="button" className="weather-record-close" onClick={close} aria-label={labels.cancel || "Close"}>×</button>
        </div>
        <div className="weather-record-grid">
          {details.map(([label, value]) => (
            <div className="weather-record-item" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        {data?.weatherContext?.inherited ? (
          <div className="weather-record-note">
            {labels.weatherInherited || "Weather is inherited from the nearest parent organization that has a location."}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const VOICE_ASSISTANT_POSITION_OPTIONS = [
  {
    value: AI_VOICE_ASSISTANT_POSITIONS.BOTTOM_RIGHT,
    labelKey: "voiceAssistantPositionBottomRight",
    Icon: SouthEastIcon,
  },
  {
    value: AI_VOICE_ASSISTANT_POSITIONS.BOTTOM_LEFT,
    labelKey: "voiceAssistantPositionBottomLeft",
    Icon: SouthWestIcon,
  },
  {
    value: AI_VOICE_ASSISTANT_POSITIONS.TOP_RIGHT,
    labelKey: "voiceAssistantPositionTopRight",
    Icon: NorthEastIcon,
  },
  {
    value: AI_VOICE_ASSISTANT_POSITIONS.TOP_LEFT,
    labelKey: "voiceAssistantPositionTopLeft",
    Icon: NorthWestIcon,
  },
];

const getHeySamaStateLabel = (state, labels) => {
  const map = {
    off: labels.heySamaOff || "Off",
    standby: labels.heySamaStandby || 'Say "Hey Sama"',
    connecting: labels.heySamaConnecting || "Connecting",
    reconnecting: labels.heySamaReconnecting || "Reconnecting",
    listening: labels.heySamaListening || "Listening",
    hearing: labels.heySamaHearing || "Hearing you",
    thinking: labels.heySamaThinking || "Thinking",
    speaking: labels.heySamaSpeaking || "Speaking",
    error: labels.heySamaError || "Needs attention",
  };
  return map[state] || map.off;
};

const HeySamaHeaderControl = ({ labels }) => {
  const { permissions } = useAuth();
  const {
    settings: browserVoiceSettings,
    runtime,
    requestEnable,
    requestDisable,
  } = useBrowserVoiceCommandSettings();

  if (!permissions?.ai_voice_command) return null;

  const enabled = browserVoiceSettings.enabled;
  const state = enabled ? runtime.state || "standby" : "off";
  const stateLabel = getHeySamaStateLabel(state, labels);
  const tooltip = runtime.error || (enabled
    ? `${labels.heySamaOn || "Hey Sama is on"} · ${stateLabel}`
    : labels.heySamaEnable || "Turn on Hey Sama live conversation");

  return (
    <Tooltip title={tooltip}>
      <button
        type="button"
        className={`hey-sama-control${enabled ? " is-on" : ""}${runtime.live ? " is-live" : ""} state-${state}`}
        aria-pressed={enabled}
        aria-label={enabled ? (labels.heySamaDisable || "Turn off Hey Sama") : (labels.heySamaEnable || "Turn on Hey Sama")}
        onClick={async () => {
          if (enabled) {
            requestDisable();
            return;
          }
          await requestEnable();
        }}
      >
        <span className="hey-sama-control-icon" aria-hidden="true">
          <HearingOutlinedIcon fontSize="small" />
          <i />
        </span>
        <span className="hey-sama-control-copy">
          <strong>{labels.heySama || "Hey Sama"}</strong>
          <small>{stateLabel}</small>
        </span>
      </button>
    </Tooltip>
  );
};

const VoiceAssistantProfileSettings = ({ labels }) => {
  const { settings, setEnabled, setPosition } = useAiVoiceAssistantSettings();
  const {
    settings: browserVoiceSettings,
    requestEnable,
    requestDisable,
  } = useBrowserVoiceCommandSettings();
  const { permissions } = useAuth();

  if (!permissions?.ai_voice_command) return null;

  return (
    <div className="profile-ai-settings">
      <div className="profile-row profile-row-static profile-ai-settings-head">
        <span className="profile-row-left">
          <GraphicEqOutlinedIcon className="profile-row-icon" />
          {labels.voiceAssistant || "Voice Assistant"}
        </span>

        <button
          type="button"
          className={`profile-ai-switch${settings.enabled ? " is-on" : ""}`}
          role="switch"
          aria-checked={settings.enabled}
          onClick={() => setEnabled(!settings.enabled)}
        >
          <span />
        </button>
      </div>

      {settings.enabled ? (
        <div
          className="profile-ai-position-grid"
          role="group"
          aria-label={labels.voiceAssistantPosition || "Voice assistant position"}
        >
          {VOICE_ASSISTANT_POSITION_OPTIONS.map(({ value, labelKey, Icon }) => {
            const label = labels[labelKey] || labelKey;
            return (
              <button
                key={value}
                type="button"
                className={`profile-ai-position-btn${settings.position === value ? " is-active" : ""}`}
                onClick={() => setPosition(value)}
                title={label}
                aria-pressed={settings.position === value}
              >
                <Icon fontSize="small" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="profile-row profile-row-static profile-ai-settings-head">
        <span className="profile-row-left">
          <HearingOutlinedIcon className="profile-row-icon" />
          {labels.heySama || "Hey Sama live conversation"}
        </span>

        <button
          type="button"
          className={`profile-ai-switch${browserVoiceSettings.enabled ? " is-on" : ""}`}
          role="switch"
          aria-checked={browserVoiceSettings.enabled}
          onClick={async () => {
            if (browserVoiceSettings.enabled) {
              requestDisable();
              return;
            }
            await requestEnable();
          }}
        >
          <span />
        </button>
      </div>
    </div>
  );
};

const UserSettings = ({ setState, theme, setTheme, logout, user, setUser, labels, anchorRect }) => {
  const [edit, setEdit] = useState({ open: false, id: 0 });
  const [changePassword, setChangePassword] = useState({ open: false, userId: 0 });
  const { lang } = useContext(LanguageContext);
  const isRtl = ["ar", "ur"].includes(lang);
  const navigate = useNavigate();

  const colors = {
    headerBackground: "var(--header-background)",
    headerColor: "var(--header-color)",
    cellBackground: "var(--cell-background)",
    cellColor: "var(--cell-color)",
    btnBackground: "rgb(219, 245, 236)",
    btnColor: "var(--accent)",
    modalBorder: "var(--modal-border)",
  };

  const { selectedSize, setSelectedSize } = useFontSize();
  const { permissions } = useAuth();
  const canChangePassword = permissions["change_user_password"];

  useEffect(() => {
    const emailField = document.querySelector(".settings-modal-email");
    if (emailField && user.email) {
      if (user.email.length > 24) {
        emailField.innerText = user.email.substring(0, 21) + "...";
      } else {
        emailField.innerText = user.email;
      }
    }
  }, [user.email]);

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: isRtl ? "left" : "right",
  });

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: isRtl ? "left" : "right",
      });
    },
    [isRtl]
  );

  const refetchUser = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(apiUrl(`user/get/${user.id}`));
      if (res.data && res.data.status === "OK") {
        setUser(res.data.data);
      } else {
        throw new Error("Received an invalid response from the server.");
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      showAlert("error", `Could not refresh user data: ${errorMessage}`);
    }
  }, [user?.id, setUser, showAlert]);

  const handleFontSizeChange = async (size) => {
    setSelectedSize(size);
    if (user?.id) {
      try {
        await axios.post(apiUrl(`user/settings`), {
          userId: user.id,
          fontSize: size,
        });
      } catch (error) {
        showAlert("error", "Failed to save font size setting.");
      }
    }
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    setUser({
      ...user,
      settings: user.settings
        ? {
          ...user.settings,
          others: {
            ...user.others,
            theme: newTheme === "light-theme" ? "light" : "dark",
          },
        }
        : { others: { theme: newTheme === "light-theme" ? "light" : "dark" } },
    });
  };

  const myProfileLabel = labels.myProfile || "My Profile";
  const popupStyle = useMemo(() => {
    const menuWidth = 304;
    const viewportPadding = 12;
    const anchorGap = 10;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : menuWidth + (viewportPadding * 2);
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
    const fallbackTop = 92;
    const anchorTop = Number.isFinite(anchorRect?.top) ? anchorRect.top : fallbackTop - 44;
    const anchorBottom = Number.isFinite(anchorRect?.bottom) ? anchorRect.bottom : fallbackTop;
    const anchorLeft = Number.isFinite(anchorRect?.left) ? anchorRect.left : viewportWidth - menuWidth - viewportPadding;
    const anchorRight = Number.isFinite(anchorRect?.right) ? anchorRect.right : viewportWidth - viewportPadding;

    let left = anchorRight - menuWidth;
    const preferredLeft = anchorLeft - menuWidth - anchorGap;
    if (preferredLeft >= viewportPadding) {
      left = preferredLeft;
    }

    left = Math.min(
      Math.max(viewportPadding, left),
      Math.max(viewportPadding, viewportWidth - menuWidth - viewportPadding)
    );

    let top = anchorBottom + 8;
    const estimatedMenuHeight = 520;
    if (top + estimatedMenuHeight > viewportHeight - viewportPadding && anchorTop > estimatedMenuHeight) {
      top = Math.max(viewportPadding, anchorTop - estimatedMenuHeight + 8);
    }

    return {
      top: `${top}px`,
      left: `${left}px`,
      right: "auto",
    };
  }, [anchorRect]);

  return (
    <div id="settings-modal" onClick={() => setState(false)}>
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      {edit.open ? (
        <EditUser
          state={edit}
          setState={setEdit}
          bg={colors.cellBackground}
          fg={colors.cellColor}
          bd={colors.modalBorder}
          ft={colors.headerBackground}
          btnPrimary={colors.btnBackground}
          btnSecondary={colors.btnColor}
          textColor={colors.headerColor}
          refresh={refetchUser}
        />
      ) : null}

      {changePassword.open ? (
        <ChangeUserPassword
          state={changePassword}
          setState={setChangePassword}
          canChangePassword={canChangePassword}
        />
      ) : null}

      <div
        id="settings-popup"
        onClick={(e) => e.stopPropagation()}
        className={`profile-dropdown-card profile-dropdown-menu ${isRtl ? "is-rtl" : ""}`}
        style={popupStyle}
      >
        <div className="profile-dropdown-header">
          <div className="profile-avatar">
            <DefaultIcon name={user.name || ""} />
          </div>

          <div
            className="settings-modal-user"
            onClick={() => {
              setState(false);
              navigate("/administration/users/details/" + user.id);
            }}
          >
            <h3 className="settings-modal-name">{user.name}</h3>
            <span className="settings-modal-email"></span>
          </div>

          <Tooltip title={labels.profileSettings} onClick={() => setEdit({ open: true, id: user.id })}>
            <SettingsOutlined className="profile-settings-icon" />
          </Tooltip>
        </div>

        <div className="profile-divider" />

        <button
          type="button"
          className="profile-row profile-row-btn"
          onClick={() => {
            setState(false);
            navigate("/administration/users/details/" + user.id);
          }}
        >
          <span className="profile-row-left">
            <PersonIcon className="profile-row-icon" />
            {myProfileLabel}
          </span>
          <ArrowIcon className="profile-row-arrow" />
        </button>

        <div className="profile-row profile-row-static">
          <span>{labels.status || "Status"}</span>
          <span className="profile-status-active">{labels.active || "Active"}</span>
        </div>

        <div className="profile-divider" />

        <div className="profile-row profile-row-static">
          <span>{labels.fontSize || "Font Size"}</span>
          <div className="profile-font-controls">
            <button
              onClick={() => handleFontSizeChange("small")}
              className={`details-control-btn-font ${selectedSize === "small" ? "active" : ""}`}
            >
              S
            </button>
            <button
              onClick={() => handleFontSizeChange("medium")}
              className={`details-control-btn-font ${selectedSize === "medium" ? "active" : ""}`}
            >
              M
            </button>
            <button
              onClick={() => handleFontSizeChange("large")}
              className={`details-control-btn-font ${selectedSize === "large" ? "active" : ""}`}
            >
              L
            </button>
          </div>
        </div>

        <VoiceAssistantProfileSettings labels={labels} />

        <button
          type="button"
          className="profile-row profile-row-btn"
          onClick={() => setChangePassword({ open: true, userId: user.id })}
        >
          <span>{labels.changePassword}</span>
          <PasswordIcon color={"var(--part-color)"} />
        </button>

        <div className="profile-divider" />

        <button
          type="button"
          className="profile-row profile-row-btn profile-row-btn--logout"
          onClick={() => logout()}
        >
          <span>{labels.logout}</span>
          <LogoutIcon className="profile-logout-icon" />
        </button>
      </div>
    </div>
  );
};

const SubOrganization = ({ organization, close, openEditModal }) => {
  const { setOrg, permissions } = useAuth();
  const canEditOrg = permissions?.["edit_organizations"];
  const open = useContext(CollapseContext);
  const navigate = useNavigate();
  const [children, setChildren] = useState({ loading: true, data: [] });

  const { lang } = useContext(LanguageContext);
  const isRtl = ["ar", "ur"].includes(lang);

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: isRtl ? "left" : "right",
  });

  const showAlert = (type, message) => {
    setAlertState({ open: true, type, message, position: isRtl ? "left" : "right" });
  };

  useEffect(() => {
    const parentId = Number(organization?.id);
    if (!Number.isInteger(parentId) || parentId < 1) {
      setChildren({ loading: false, data: [] });
      return;
    }

    setChildren({ loading: true, data: [] });

    axios
      .get(apiUrl(`organization/filtered/${parentId}`))
      .then((res) => {
        if (res.data && res.data.status === "OK") {
          setChildren({
            loading: false,
            data: Array.isArray(res.data.data)
              ? res.data.data.map(normalizeOrganizationListRow)
              : [],
          });
        } else {
          showAlert("error", "Failed to retrieve sub-organization details.");
          setChildren({ loading: false, data: [] });
        }
      })
      .catch((err) => {
        const errorResponse = err.response;
        if (errorResponse) {
          if (errorResponse.status === 400 && errorResponse.data && errorResponse.data.errors) {
            const errorMessage = errorResponse.data.errors.map((e) => e.msg).join(" ");
            showAlert("error", errorMessage);
          } else {
            showAlert("error", "An unexpected server error occurred. Please try again.");
          }
        } else {
          showAlert("error", "Network error. Please check your connection.");
        }
        setChildren({ loading: false, data: [] });
      });
  }, [organization?.id]);

  return (
    <div id="settings-modal" onClick={close}>
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div
        id="org-popup"
        onClick={(e) => e.stopPropagation()}
        className="org-dropdown-card"
        style={{ insetInlineStart: open ? 56 : "18%" }}
      >
        <div className="org-dropdown-header">
          <span className="org-dd-avatar">
            {organization.icon ? (
              <img className="org-img" alt="" src={STATIC_ICONS + organization.icon} />
            ) : (
              <OrganizationIcon color={"var(--org-dd-icon-color)"} />
            )}
          </span>

          <div className="org-dd-meta">
            <h3
              className="org-dd-title"
              onClick={() => {
                close();
                navigate("/administration/organizations/details/" + organization.id);
              }}
            >
              {organization.name}
            </h3>

            <span className="org-dd-subtitle">{organization.description || ""}</span>
          </div>

          {canEditOrg && (
            <Tooltip title="Organization Settings">
              <div className="icon-btn icon-btn--sm" onClick={() => openEditModal()}>
                <SettingsOutlined fontSize="small" />
              </div>
            </Tooltip>
          )}
        </div>

        <div className="org-dd-divider" />

        <div className="org-dropdown-content">
          {children.loading ? (
            <div className="org-dd-empty">Loading...</div>
          ) : children.data?.length > 0 ? (
            children.data?.map((child, idx) => (
              <button
                type="button"
                className="org-dd-item"
                key={child.id || idx}
                onClick={() => {
                  setOrg({ id: child.id, name: child.name });
                  close();
                }}
              >
                <span className="org-dd-item-left">
                  <span className="org-dd-item-avatar">
                    {child.icon ? (
                      <img className="org-img" alt="" src={STATIC_ICONS + child.icon} />
                    ) : (
                      <OrganizationIcon color={"var(--org-dd-item-fallback-icon)"} />
                    )}
                  </span>

                  <span className="org-dd-item-name">{child.name}</span>
                </span>

                <span className="org-dd-item-stats">
                  <span className="org-dd-stat">
                    <DeviceIcon color={"var(--org-dd-stat-icon)"} /> {child.device_count}
                  </span>
                  <span className="org-dd-stat">
                    <UsersIcons color={"var(--org-dd-stat-icon)"} /> {child.user_count}
                  </span>
                  <span className="org-dd-stat">
                    <OrganizationIcon color={"var(--org-dd-stat-icon)"} /> {child.sub_organizations}
                  </span>
                </span>
              </button>
            ))
          ) : (
            <div className="org-dd-empty">No Data.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const useOrganizationDetails = (org, user, setOrg, showAlert, refreshKey) => {
  const [organization, setOrganization] = useState({
    loading: true,
    data: null,
    currentOrgId: null,
  });

  useEffect(() => {
    const orgId = Number(org?.id);

    if (!Number.isInteger(orgId) || orgId < 1 || !user?.id) {
      setOrganization({ loading: false, data: null, currentOrgId: null });
      return;
    }

    if (organization.currentOrgId === orgId && refreshKey === 0) return;

    setOrganization((prev) => ({ ...prev, loading: true }));

    axios
      .get(apiUrl(`organization/get/${orgId}`))
      .then((res) => {
        if (res.data && res.data.status === "OK") {
          const organizationData = res.data.data;

          if (organizationData && organizationData.hierarchy) {
            let hierarchy = organizationData.hierarchy;
            let user_hierarchy = [];
            let user_or = false;

            for (let i = 0; i < hierarchy.length - 1; i++) {
              if (hierarchy[i].id === user.organization_id) user_or = true;
              if (user_or) user_hierarchy.push(hierarchy[i]);
            }
            organizationData.hierarchy = user_hierarchy;
          }

          setOrganization({
            loading: false,
            data: organizationData,
            currentOrgId: orgId,
          });
        } else {
          showAlert("error", "Failed to retrieve organization details.");
          setOrganization({
            loading: false,
            data: null,
            currentOrgId: null,
          });
        }
      })
      .catch((err) => {
        const errorResponse = err.response;
        const fallbackOrgId = Number(user?.organization_id);
        const canFallbackToUserOrg =
          Number.isInteger(fallbackOrgId) &&
          fallbackOrgId > 0 &&
          fallbackOrgId !== orgId;

        if (errorResponse) {
          if (errorResponse.status === 404 && canFallbackToUserOrg) {
            console.warn(
              `[Header] Selected organization ${orgId} is not accessible for user ${user?.id}. Falling back to organization ${fallbackOrgId}.`
            );
            setOrganization({
              loading: true,
              data: null,
              currentOrgId: null,
            });
            setOrg({ id: fallbackOrgId, name: user?.organization_name || "" });
            return;
          }

          if (errorResponse.status === 404) {
            showAlert("error", `Organization with ID ${orgId} not found.`);
          } else if (errorResponse.status === 400 && errorResponse.data && errorResponse.data.errors) {
            const errorMessage = errorResponse.data.errors.map((e) => e.msg).join(" ");
            showAlert("error", errorMessage);
          } else {
            showAlert("error", "An unexpected server error occurred. Please try again.");
          }
        } else {
          showAlert("error", "Network error. Please check your connection.");
        }

        setOrganization({
          loading: false,
          data: null,
          currentOrgId: null,
        });
      });
  }, [org?.id, user?.id, user?.organization_id, user?.organization_name, organization.currentOrgId, setOrg, showAlert, refreshKey]);

  return organization;
};

const Header = ({ theme, setTheme }) => {
  const { user, setUser, logOut, org, setOrg, permissions } = useAuth();
  const [openInfo, setOpenInfo] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [openOrganization, setOpenOrganization] = useState(false);
  const [openEditOrganization, setOpenEditOrganization] = useState(false);
  const [openWeatherRecord, setOpenWeatherRecord] = useState(false);
  const { lang, setLang } = useContext(LanguageContext);
  const labels = (headerData[lang] || headerData.en).header;
  const systemNotificationsT =
    systemNotificationsTranslations[lang] || systemNotificationsTranslations.en;
  const isRtl = ["ar", "ur"].includes(lang);
  const canEditOrg = permissions?.["edit_organizations"];
  const navigate = useNavigate();
  const { mode, toggle } = useAppTheme();
  const [orgRefreshKey, setOrgRefreshKey] = useState(0);
  const isSuperUser = String(user?.role || "").toLowerCase() === "super";
  const [openSystemNotifications, setOpenSystemNotifications] = useState(false);
  const [systemNotifications, setSystemNotifications] = useState({ loading: false, items: [], unread: 0 });
  const [selectedNotification, setSelectedNotification] = useState(null);
  const notificationPanelRef = useRef(null);
  const profileButtonRef = useRef(null);
  const [now, setNow] = useState(() => new Date());
  const [profileAnchorRect, setProfileAnchorRect] = useState(null);

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: isRtl ? "left" : "right",
  });

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: isRtl ? "left" : "right",
      });
    },
    [isRtl]
  );

  useEffect(() => {
    let timeoutId;

    const tick = () => {
      setNow(new Date());
      const delay = 1000 - (Date.now() % 1000);
      timeoutId = window.setTimeout(tick, delay || 1000);
    };

    tick();

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  const clockLocale = useMemo(() => {
    if (lang === "ar") return "ar-SY";
    if (lang === "ur") return "ur-PK";
    return "en-US";
  }, [lang]);

  const dateText = useMemo(
    () =>
      new Intl.DateTimeFormat(clockLocale, {
        weekday: "short",
        month: "short",
        day: "2-digit",
        year: "numeric",
      }).format(now),
    [clockLocale, now]
  );

  const timeParts = useMemo(() => {
    const parts = new Intl.DateTimeFormat(clockLocale, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(now);

    const getPart = (type, fallback = "00") =>
      parts.find((part) => part.type === type)?.value || fallback;

    return {
      main: `${getPart("hour")}:${getPart("minute")}`,
      seconds: getPart("second"),
    };
  }, [clockLocale, now]);

  const { loading: organizationLoading, data: organizationData } = useOrganizationDetails(org, user, setOrg, showAlert, orgRefreshKey);
  const isOrgPlaceholder = !organizationData;
  const [organizationWeather, setOrganizationWeather] = useState({ loading: false, data: null });
  const weatherRequestIdRef = useRef(0);

  const fetchOrganizationWeather = useCallback(async (organizationId, { silent = false } = {}) => {
    const normalizedId = Number(organizationId);
    if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
      setOrganizationWeather({ loading: false, data: null });
      return;
    }
    const requestId = ++weatherRequestIdRef.current;
    if (!silent) setOrganizationWeather((prev) => ({ ...prev, loading: true }));
    try {
      const res = await axios.get(apiUrl(`organization/weather/${normalizedId}`));
      if (requestId !== weatherRequestIdRef.current) return;
      setOrganizationWeather({ loading: false, data: res?.data?.data || null });
    } catch (_error) {
      if (requestId !== weatherRequestIdRef.current) return;
      setOrganizationWeather((prev) => ({ loading: false, data: prev.data || null }));
    }
  }, []);


  const handleWeatherRecordOpen = useCallback(async () => {
    const organizationId = Number(organizationData?.id);
    if (!Number.isInteger(organizationId) || organizationId <= 0) return;

    try {
      const res = await axios.get(apiUrl(`organization/weather/${organizationId}`));
      const latest = res?.data?.data || null;
      if (latest) setOrganizationWeather({ loading: false, data: latest });
      if (latest?.record) setOpenWeatherRecord(true);
    } catch (_error) {
      if (organizationWeather.data?.record) setOpenWeatherRecord(true);
    }
  }, [organizationData?.id, organizationWeather.data]);

  useEffect(() => {
    const organizationId = Number(organizationData?.id);
    if (!Number.isInteger(organizationId) || organizationId <= 0) {
      weatherRequestIdRef.current += 1;
      setOrganizationWeather({ loading: false, data: null });
      setOpenWeatherRecord(false);
      return undefined;
    }

    weatherRequestIdRef.current += 1;
    setOrganizationWeather({ loading: true, data: null });
    setOpenWeatherRecord(false);
    fetchOrganizationWeather(organizationId, { silent: true });

    let evtSource = null;
    try {
      evtSource = new EventSource(apiUrl(`organization/weather-stream/${organizationId}`), {
        withCredentials: true,
      });
      evtSource.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (Number(organizationData?.id) !== organizationId) return;
          setOrganizationWeather({ loading: false, data: payload?.data || null });
        } catch (_error) {}
      };
      evtSource.onerror = () => {
        // EventSource reconnects automatically. Keep the last valid record on screen.
      };
    } catch (_error) {}

    // Low-frequency REST fallback for proxies/browsers that block SSE.
    const fallbackIntervalId = window.setInterval(() => {
      fetchOrganizationWeather(organizationId, { silent: true });
    }, 60 * 1000);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchOrganizationWeather(organizationId, { silent: true });
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      evtSource?.close();
      window.clearInterval(fallbackIntervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchOrganizationWeather, organizationData?.id, organizationData?.location_id]);

  const handleOrganizationUpdate = (updatedOrg) => {
    if (org?.id === updatedOrg.id) {
      setOrg({ id: updatedOrg.id, name: updatedOrg.name });
    }
    setOrgRefreshKey(prev => prev + 1);

    setOpenEditOrganization(false);
  };

  const fetchSystemNotifications = useCallback(async () => {
    if (!isSuperUser) return;
    setSystemNotifications((prev) => ({ ...prev, loading: true }));
    try {
      const res = await axios.get(apiUrl(`device/plan-notifications`), {
        params: { status: "all", page: 1, pageSize: 10 },
      });
      const items = Array.isArray(res?.data?.data) ? res.data.data : [];
      const unread = Number(res?.data?.summary?.unread) || 0;
      setSystemNotifications({ loading: false, items, unread });
    } catch (_err) {
      setSystemNotifications((prev) => ({ ...prev, loading: false }));
    }
  }, [isSuperUser]);

  const markSystemNotificationRead = useCallback(async (notificationId) => {
    if (!notificationId) return;
    try {
      await axios.post(apiUrl(`device/plan-notifications/read/${notificationId}`));
      setSystemNotifications((prev) => ({
        ...prev,
        unread: Math.max(0, prev.unread - 1),
        items: prev.items.map((item) =>
          Number(item.id) === Number(notificationId) ? { ...item, status: "read" } : item
        ),
      }));
    } catch (_err) { }
  }, []);

  useEffect(() => {
    if (!isSuperUser) return undefined;
    fetchSystemNotifications();
    const intervalId = setInterval(() => {
      fetchSystemNotifications();
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchSystemNotifications, isSuperUser]);

  useEffect(() => {
    if (!openSystemNotifications) return;

    const handleOutsideClick = (event) => {
      if (notificationPanelRef.current && !notificationPanelRef.current.contains(event.target)) {
        setOpenSystemNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openSystemNotifications]);

  const unreadSystemNotificationsCount = systemNotifications.unread;
  const notificationPreviewItems = useMemo(
    () =>
      systemNotifications.items
        .slice(0, 5)
        .map((item) => localizeSystemNotification(item, systemNotificationsT)),
    [systemNotifications.items, systemNotificationsT]
  );

  const toggleMobileNavigation = useCallback(() => {
    window.dispatchEvent(new Event("sama:sidenav:toggle-mobile-drawer"));
  }, []);

  const navigateToSystemNotifications = () => {
    setOpenSystemNotifications(false);
    navigate("/administration/system-notifications");
  };

  const closeNotificationDetails = useCallback(() => {
    setSelectedNotification(null);
  }, []);

  const measureProfileAnchor = useCallback(() => {
    const rect = profileButtonRef.current?.getBoundingClientRect();
    if (!rect) return null;

    return {
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
  }, []);

  useEffect(() => {
    if (!openSettings) return undefined;

    const syncProfileAnchor = () => {
      setProfileAnchorRect(measureProfileAnchor());
    };

    syncProfileAnchor();
    window.addEventListener("resize", syncProfileAnchor);
    window.addEventListener("scroll", syncProfileAnchor, true);

    return () => {
      window.removeEventListener("resize", syncProfileAnchor);
      window.removeEventListener("scroll", syncProfileAnchor, true);
    };
  }, [measureProfileAnchor, openSettings]);

  const markSelectedNotificationRead = useCallback(async () => {
    if (!selectedNotification || selectedNotification.status !== "unread") return;
    await markSystemNotificationRead(selectedNotification.id);
    setSelectedNotification((prev) => (prev ? { ...prev, status: "read" } : prev));
  }, [markSystemNotificationRead, selectedNotification]);

  const viewAllFromDetailsPopup = () => {
    setSelectedNotification(null);
    navigateToSystemNotifications();
  };

  const handleNotificationBellClick = () => {
    if (!isSuperUser && systemNotifications.items.length === 0) {
      navigate("/notifications");
      return;
    }

    setOpenSystemNotifications((prev) => {
      const next = !prev;
      if (next) fetchSystemNotifications();
      return next;
    });
  };

  const handleNotificationPreviewClick = (item) => {
    if (!item) return;
    setSelectedNotification(localizeSystemNotification(item, systemNotificationsT));
    setOpenSystemNotifications(false);
  };

  return (
    <div className="config-header">
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div className="header-left">
        <button
          type="button"
          className="header-mobile-menu-btn"
          onClick={toggleMobileNavigation}
          aria-label="Open navigation"
        >
          <MenuIcon fontSize="small" />
        </button>

        <div
          className={`org-header${isOrgPlaceholder ? " org-header--loading" : ""}`}
          aria-busy={organizationLoading}
        >
          {organizationData?.hierarchy &&
            organizationData.hierarchy.map((p, i) => (
              <div key={p.id || i} className="org-breadcrumb-item">
                <span
                  className="org-link"
                  onClick={() => {
                    setOrg({ id: p.id, name: p.name });
                  }}
                >
                  {p.name}
                </span>
                <span className="org-breadcrumb-separator">/</span>
              </div>
            ))}

          <button
            type="button"
            className="org-selector-card"
            onClick={() => {
              if (!organizationData) return;
              setOpenOrganization(true);
            }}
            aria-label="Select organization"
            disabled={!organizationData}
          >
            <span className="org-selector-name" title={organizationData?.name || "Main"}>
              {organizationData?.name || "Main"}
            </span>
            <ArrowIcon className="org-selector-arrow" />
          </button>

          <div className="org-separator" />
        </div>

        {canEditOrg && (
          <Tooltip title={labels.organizationSettings || "Organization Settings"}>
            <div
              className="icon-btn"
              onClick={() => {
                if (!organizationData?.id) return;
                setOpenEditOrganization(true);
              }}
            >
              <HeaderSettingsIcon fontSize="small" />
            </div>
          </Tooltip>
        )}
      </div>

      <div className="header-right">
        {/* <div className="search-container">
          <SearchIcon className="search-icon-inside" />
          <input type="text" placeholder="Type here..." className="search-input" />
        </div> */}

        <div className={`header-live-clock${isRtl ? " is-rtl" : ""}`} aria-label={labels.localTime || "Local time"}>
          <div className="header-live-clock-card header-live-clock-card--date">
            <span className="header-live-clock-label">{labels.date || "Date"}</span>
            <span className="header-live-clock-date">{dateText}</span>
          </div>

          <div className="header-live-clock-card header-live-clock-card--time">
            <span className="header-live-clock-label">{labels.time || "Time"}</span>
            <span className="header-live-clock-time">
              <span className="header-live-clock-main">{timeParts.main}</span>
              <span className="header-live-clock-seconds">:{timeParts.seconds}</span>
            </span>
          </div>
        </div>

        {(organizationWeather.loading || organizationWeather.data) ? (
          <button
            type="button"
            className={`header-weather${organizationWeather.loading && !organizationWeather.data ? " is-loading" : ""}`}
            title={organizationWeather.data?.location?.name || organizationWeather.data?.location?.address || labels.weather || "Current weather"}
            aria-label={labels.openLatestWeatherRecord || labels.weather || "Open latest weather record"}
            onClick={handleWeatherRecordOpen}
            disabled={!organizationWeather.data?.record}
          >
            <div className="header-weather-metric">
              <DeviceThermostatOutlinedIcon />
              <span>
                {Number.isFinite(Number(organizationWeather.data?.temperatureC))
                  ? `${Math.round(Number(organizationWeather.data.temperatureC))}°C`
                  : "--"}
              </span>
            </div>
            <div className="header-weather-divider" />
            <div className="header-weather-metric">
              <WaterDropOutlinedIcon />
              <span>
                {Number.isFinite(Number(organizationWeather.data?.humidityPercent))
                  ? `${Math.round(Number(organizationWeather.data.humidityPercent))}%`
                  : "--"}
              </span>
            </div>
          </button>
        ) : null}

        <HeySamaHeaderControl labels={labels} />

        <Tooltip title={mode === "dark" ? "Light Mode" : "Dark Mode"}>
          <div className="icon-btn" onClick={toggle}>
            {mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </div>
        </Tooltip>


        <div className="lang-wrap">
          <Select
            value={lang}
            size="small"
            variant="standard"
            disableUnderline
            onChange={(e) => setLang(e.target.value)}
            className="lang-select"
            MenuProps={{
              PaperProps: {
                className: "header-lang-menu",
              },
            }}
          >
            <MenuItem value="en">EN</MenuItem>
            <MenuItem value="ar">AR</MenuItem>
            <MenuItem value="ur">UR</MenuItem>
          </Select>
        </div>

        <div className="header-notification-wrap" ref={notificationPanelRef}>
          <Tooltip
            title={openSystemNotifications ? "" : (systemNotifications.items.length > 0 ? systemNotificationsT.title : "Notifications")}
            disableHoverListener={openSystemNotifications}
          >
            <div className="icon-btn header-notification-btn" onClick={handleNotificationBellClick}>
              <NotificationsNoneIcon fontSize="small" />
              {unreadSystemNotificationsCount > 0 ? (
                <span className="header-notification-badge">
                  {unreadSystemNotificationsCount > 99 ? "99+" : unreadSystemNotificationsCount}
                </span>
              ) : null}
            </div>
          </Tooltip>

          {openSystemNotifications ? (
            <div
              className={`header-notification-panel${isRtl ? " is-rtl" : ""}`}
              dir={isRtl ? "rtl" : "ltr"}
            >
              <div className="header-notification-panel-head">
                <h4>{systemNotificationsT.title}</h4>
                <button
                  type="button"
                  className="header-notification-view-all"
                  onClick={navigateToSystemNotifications}
                >
                  {systemNotificationsT.viewAll}
                </button>
              </div>

              <div className="header-notification-list">
                {systemNotifications.loading ? (
                  <div className="header-notification-empty">{systemNotificationsT.loading}</div>
                ) : notificationPreviewItems.length === 0 ? (
                  <div className="header-notification-empty">{systemNotificationsT.empty}</div>
                ) : (
                  notificationPreviewItems.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      className={`header-notification-item${item.status === "unread" ? " unread" : ""}`}
                      onClick={() => handleNotificationPreviewClick(item)}
                    >
                      <div className="header-notification-item-title">
                        {item.title || systemNotificationsT.notificationText?.fallbackTitle || "Notification"}
                      </div>
                      <div className="header-notification-item-message">{item.message || ""}</div>
                      <div className="header-notification-item-time">
                        {formatSystemNotificationTimestamp(item.created_at, lang)}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>

        <Tooltip title={labels.projectInfo || "Project Info"}>
          <div className="icon-btn" onClick={() => setOpenInfo(true)}>
            <InfoIcon fontSize="small" />
          </div>
        </Tooltip>

        <Tooltip title={user?.name || "Profile"}>
          <IconButton
            ref={profileButtonRef}
            onClick={() => {
              setProfileAnchorRect(measureProfileAnchor());
              setOpenSettings(true);
            }}
            size="small"
            className="header-profile-btn"
          >
            <PersonIcon />
          </IconButton>
        </Tooltip>
      </div>

      {openOrganization && organizationData && (
        <SubOrganization
          organization={organizationData}
          close={() => setOpenOrganization(false)}
          openEditModal={() => {
            setOpenOrganization(false);
            setOpenEditOrganization(true);
          }}
        />
      )}

      {openSettings ? (
        <UserSettings
          setState={setOpenSettings}
          logout={logOut}
          theme={theme}
          setTheme={setTheme}
          user={user}
          setUser={setUser}
          labels={labels}
          anchorRect={profileAnchorRect}
        />
      ) : null}

      {openInfo && <ProjectInfo close={() => setOpenInfo(false)} labels={labels} />}

      {openWeatherRecord && organizationWeather.data?.record ? (
        <WeatherRecordPopup
          close={() => setOpenWeatherRecord(false)}
          data={organizationWeather.data}
          labels={labels}
          locale={clockLocale}
          isRtl={isRtl}
        />
      ) : null}

      {openEditOrganization && organizationData && (
        <EditOrganization
          state={{ open: openEditOrganization, id: organizationData.id }}
          setState={setOpenEditOrganization}
          onSuccess={handleOrganizationUpdate}
          initialData={organizationData}
        />
      )}

      <NotificationDetailsPopup
        open={Boolean(selectedNotification)}
        notification={selectedNotification}
        onClose={closeNotificationDetails}
        onMarkRead={markSelectedNotificationRead}
        onViewAll={viewAllFromDetailsPopup}
      />
    </div>
  );
};

export default Header;
